import { useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import { OnThisPageItem } from '../../types/onThisPage';
import { applyHeadingAnchors } from '../../utils/headingToc';
import { youtubeVideoId } from '../../utils/youtube';

interface ResourceContentProps {
  content: string;
  onHeadings?: (items: OnThisPageItem[]) => void;
}

/** Keep only YouTube iframes and wrap them in a 16:9 player. */
function enhanceYoutubeEmbeds(root: HTMLElement) {
  const iframes = Array.from(root.querySelectorAll('iframe'));
  for (const iframe of iframes) {
    const videoId = youtubeVideoId(iframe.getAttribute('src') || '');
    if (!videoId) {
      iframe.remove();
      continue;
    }

    const title = iframe.getAttribute('title') || 'YouTube video';
    const wrapper = document.createElement('div');
    wrapper.className = 'relative w-full my-6 overflow-hidden rounded-lg bg-gray-100';
    wrapper.style.paddingBottom = '56.25%';

    const next = document.createElement('iframe');
    next.src = `https://www.youtube-nocookie.com/embed/${videoId}`;
    next.className = 'absolute top-0 left-0 h-full w-full';
    next.title = title;
    next.loading = 'lazy';
    next.setAttribute(
      'allow',
      'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
    );
    next.setAttribute('allowfullscreen', '');
    next.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
    wrapper.appendChild(next);

    const figure = iframe.closest('figure');
    (figure || iframe).replaceWith(wrapper);
  }
}

export default function ResourceContent({ content, onHeadings }: ResourceContentProps) {
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!contentRef.current) return;

    const sanitized = DOMPurify.sanitize(content, {
      ALLOWED_TAGS: [
        'p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'ul', 'ol', 'li', 'a', 'img', 'blockquote', 'code', 'pre', 'table',
        'thead', 'tbody', 'tr', 'th', 'td', 'div', 'span', 'figure', 'iframe',
      ],
      ALLOWED_ATTR: [
        'href', 'src', 'alt', 'title', 'class', 'id', 'width', 'height',
        'allow', 'allowfullscreen', 'frameborder', 'loading', 'referrerpolicy',
      ],
      ADD_ATTR: ['allowfullscreen', 'referrerpolicy'],
    });
    contentRef.current.innerHTML = sanitized;

    enhanceYoutubeEmbeds(contentRef.current);

    const images = contentRef.current.querySelectorAll('img');
    images.forEach((img) => {
      img.classList.add('max-w-full', 'h-auto', 'rounded-lg', 'my-4');
      img.loading = 'lazy';
    });

    const links = contentRef.current.querySelectorAll('a');
    links.forEach((link) => {
      link.classList.add('text-primary', 'hover:underline');
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
    });

    onHeadings?.(applyHeadingAnchors(contentRef.current));
  }, [content, onHeadings]);

  return (
    <div
      ref={contentRef}
      className="prose prose-lg max-w-none
        prose-headings:text-gray-900
        prose-headings:scroll-mt-24
        prose-p:text-gray-700
        prose-a:text-primary
        prose-strong:text-gray-900
        prose-ul:text-gray-700
        prose-ol:text-gray-700
        prose-li:text-gray-700
        prose-blockquote:border-l-primary
        prose-blockquote:text-gray-600
      "
    />
  );
}
